import { motion } from "framer-motion";
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";
import { HiChevronDown, HiChevronUp } from "react-icons/hi2";
import { MdOutlineDraw } from "react-icons/md";
import ColorBox from "../../../../Components/ColorBox/ColorBox";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const AccordionItem = ({
  item = {},
  expandedItem,
  setExpandedItem,
  editNameMode,
  newZone,
  setEditNameMode,
  handleNameChange,
  setColorPicker,
  colorPicker,
  handleDeleteZone,
  zone,
  handleZoneTypeChange,
  isDrawing,
  drawNewZoneArea,
  disableZoneSave,
  handleCancelDrawing,
  createZone,
  zoneNameField,
  setNewZone,
  setZone,
  handleColorChange,
}) => {
  const { t } = useTranslation("carrier");
  const isOpen = expandedItem === item?.id && !newZone;
  const isDisabled = !!newZone;

  return (
    <div
      className="border-l-4 my-1 rounded-md shadow-elegant text-ui-text "
      style={{ borderColor: item.color }}
    >
      <div
        className={`flex justify-between items-center p-2 cursor-pointer`}
        onClick={() => {
          setExpandedItem(isOpen ? null : item?.id);
          setZone(item);
        }}
      >
        <div className="flex items-center gap-2 w-4/5 relative">
          {editNameMode !== item?.id && newZone.id !== item?.id ? (
            <div
              className={`relative w-full h-full flex ${
                isDisabled
                  ? "pointer-events-none opacity-30 cursor-not-allowed"
                  : ""
              }`}
            >
              <span className="text-sm font-medium truncate max-w-[40%]">
                {item?.name}
              </span>
              <CiEdit
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setZone(item);
                  setEditNameMode(item?.id);
                }}
              />
              <div className="absolute left-[60%] -bottom-2">
                {isOpen ? <HiChevronUp /> : <HiChevronDown />}
              </div>
            </div>
          ) : (
            <div className="w-full flex" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                className="border border-frost rounded p-1 text-sm w-full outline-none mr-1"
                value={newZone ? newZone.name : zone.name}
                ref={zoneNameField}
                onChange={(e) => {
                  e.stopPropagation();
                  if (newZone) {
                    setNewZone({
                      ...newZone,
                      name: e.target.value,
                    });
                  } else {
                    setZone({
                      ...zone,
                      name: e.target.value,
                    });
                  }
                }}
              />
              <button
                className="text-xs w-16 h-7 flex items-center justify-center rounded-md bg-ui-inverted text-white"
                onClick={newZone ? createZone : handleNameChange}
                disabled={disableZoneSave}
              >
                {t("common:actions.save")}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 relative">
          {colorPicker === item.id && (
            <div
              className="w-fit absolute z-20 right-[15px] top-[35px]"
              onClick={(e) => e.stopPropagation()}
            >
              <ColorBox
                handleColorChange={(color) => handleColorChange(color, item)}
              />
            </div>
          )}

          <LightTooltip placement="top" arrow title={t("zones.selectZoneColour")}>
            <div
              className={`w-5 h-5 rounded  ${
                isDisabled && item?.id !== newZone.id
                  ? "pointer-events-none opacity-30 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              style={{ backgroundColor: item.color }}
              onClick={(e) => {
                e.stopPropagation();
                setColorPicker(colorPicker !== item?.id ? item?.id : false);
              }}
            ></div>
          </LightTooltip>
          <MdDeleteOutline
            className={`text-ui-text-muted  ${
              isDisabled && item?.id !== newZone.id
                ? "pointer-events-none opacity-30 cursor-not-allowed"
                : "cursor-pointer"
            }`}
            onClick={(e) => handleDeleteZone(e, item?.id)}
            size={20}
          />
        </div>
      </div>
      {editNameMode !== item?.id && newZone.id !== item?.id && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
          className="overflow-hidden"
        >
          <div className="w-full p-3 border-t text-sm text-ui-text-muted space-y-3">
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={
                    newZone
                      ? newZone.type?.includes("PICKUP")
                      : zone.type?.includes("PICKUP")
                  }
                  onChange={() => handleZoneTypeChange("PICKUP", item?.id)}
                  className="w-4 h-4 accent-black"
                />
                <span className="text-xs font-medium">{t("zones.pickUp")}</span>
              </label>

              <label className="flex items-center space-x-2  px-2 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={
                    newZone
                      ? newZone.type?.includes("DELIVERY")
                      : zone.type?.includes("DELIVERY")
                  }
                  onChange={() => handleZoneTypeChange("DELIVERY", item?.id)}
                  className="w-4 h-4 accent-black"
                />
                <span className="text-xs font-medium">{t("zones.delivery")}</span>
              </label>
            </div>

            <div>
              <span>
                {t("zones.regionsSelected", { regions: item.coordinates?.length || "-" })}
              </span>
            </div>
            {isDrawing.id !== item?.id ? (
              <button
                className="w-full mt-3 p-2 bg-ui-inverted text-white rounded flex items-center justify-center"
                onClick={() => drawNewZoneArea(item)}
              >
                <MdOutlineDraw className="mr-2" /> {t("zones.clickToDrawOnMap")}
              </button>
            ) : (
              <button
                className="w-full mt-2 p-2 bg-danger text-white rounded"
                onClick={handleCancelDrawing}
              >
                {t("zones.cancelDrawing")}
              </button>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AccordionItem;
